import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { APP_ROUTES } from "../utils/constants"
import authService from "../services/authService"
import { getCredentials } from "../services/userApis"
import { useSocket } from "../contexts/SocketContext"

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null)
    const { socket } = useSocket()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const navigate = useNavigate()
    const isCheckingRef = useRef(false)

    // Check authentication status
    const checkAuth = useCallback(async () => {
        if (isCheckingRef.current) {
            return false
        }

        try {
            isCheckingRef.current = true
            setLoading(true)
            setError(null)

            if (!authService.isTokenValid()) {
                setUser(null)
                setLoading(false)
                return false
            }

            const res = await getCredentials()
            const userData = res.data

            setUser(userData)
            setLoading(false)
            return true
        } catch (err) {
            console.error("Authentication check failed:", err)
            setError(err.message || "Authentication failed")
            setUser(null)
            setLoading(false)

            if (err?.response?.status === 401 || err?.response?.status === 403) {
                authService.logout()
                navigate(APP_ROUTES.SIGN_IN)
            }

            return false
        } finally {
            isCheckingRef.current = false
        }
    }, [navigate])

    // Login function
    const login = useCallback(async (credentials) => {
        try {
            setLoading(true)
            setError(null)

            const { token, refreshToken, expiresIn } = credentials

            if (!token) {
                throw new Error("No token received")
            }

            authService.setToken(token, expiresIn)
            if (refreshToken) {
                authService.setRefreshToken(refreshToken)
            }

            const res = await getCredentials()
            const userData = res.data
            setUser(userData)

            setLoading(false)
            return true
        } catch (err) {
            console.error("Login failed:", err)
            setError(err.message || "Login failed")
            setLoading(false)
            return false
        }
    }, [])

    // Logout function
    const logout = useCallback(() => {
        socket?.disconnect()

        authService.logout()

        setUser(null)
        setError(null)

        window.location.href = APP_ROUTES.SIGN_IN
    }, [navigate])

    // Refresh token
    const refreshToken = useCallback(async () => {
        try {
            const newToken = await authService.refreshToken()
            if (newToken) {
                await checkAuth()
            }
            return newToken
        } catch (err) {
            console.error("Token refresh failed:", err)
            logout()
            throw err
        }
    }, [checkAuth, logout])

    // Initialize authentication on mount (run once)
    useEffect(() => {
        if (!user && authService.isTokenValid()) {
            checkAuth()
        } else {
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const value = {
        user,
        setUser,
        loading,
        error,
        isAuthenticated: !!user && authService.isTokenValid(),
        login,
        logout,
        checkAuth,
        hasRole: authService.hasRole,
        hasPermission: authService.hasPermission,
        getTokenPayload: authService.getTokenPayload,
        refreshToken,
        getToken: authService.getToken,
        isTokenValid: authService.isTokenValid,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider")
    }
    return context
}
